import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { StructureList } from '../../shared/models/structure-list';
import { DocumentService } from '../../shared/services/document.service';
import { WebdokuService } from '../../web-doku/webdoku.service';
import { SelectionArguments } from '../../shared/models/selection-arguments';
import { LdwRouterService } from '../../route/ldw-router.service';
import { SettingsService } from '../../shared/settings/settings.service';
import { StructureType } from '../../shared/enums/structure-type';
import { DetailPageBase } from '../../shared/components/detailpage-base/detail-page-base';
import { ActivatedRoute, Router } from '@angular/router';
import { InfoService } from '../../shared/services/info.service';
import { combineLatest, Observable, of } from 'rxjs';
import { switchMap, tap, map, distinctUntilChanged } from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { RBCheck } from '@ldw/roesberg';
import { SearchOptions } from '../../search/search-options';
import { SearchService } from '../../search/search.service';
import { AppUrls } from '../../route/app-urls';
import { SplitViewObserverService } from '../../shared/components/split-view/split-view-observer.service';
import { SelectionType } from '../../shared/enums/selection-type';

@Component({
    selector: 'ldw-document-navigation-page',
    templateUrl: './document-navigation-page.component.html',
    styleUrls: ['./document-navigation-page.component.css'],
    standalone: false
})
export class DocumentNavigationPageComponent extends DetailPageBase implements OnInit, OnDestroy {
  private searchService = inject(SearchService);
  private fb = inject(UntypedFormBuilder);
  private splitviewObserver = inject(SplitViewObserverService);

  form: UntypedFormGroup = new UntypedFormGroup({});
  searchVisible = false;
  webDokuId = '';
  lastQuery = '';

  constructor() {
    const settingsService = inject(SettingsService);
    const documentService = inject(DocumentService);
    const webdokuService = inject(WebdokuService);
    const routerService = inject(LdwRouterService);
    const route = inject(ActivatedRoute);
    const router = inject(Router);
    const infoService = inject(InfoService);

    super(settingsService, documentService, webdokuService, routerService, route, router, infoService);
    this.updateRouteOnSelection = false;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      searchField: ['', [Validators.required]],
    });

    let nodeId: string; // current node in regular selection mode
    let selectedId: string; // last selected item in regular selection mode
    let query: string; // query string if in search mode
    let preventRequery: string;
    this.webDokuId = '';
    this.wdSubscription.add(
      combineLatest([this.route.paramMap, this.route.queryParamMap])
        .pipe(
          map(([paramMap, queryParamMap]) => {
            nodeId = paramMap.get('nodeId') ?? '';
            selectedId = queryParamMap.get('selectedId') ?? '';
            query = queryParamMap.get('query') ?? '';
            preventRequery = queryParamMap.get('pq') ?? '';
            this.webDokuId = paramMap.get('wd') ?? '';
            return {nodeId, selectedId, query, preventRequery, webDokuId: this.webDokuId};
          }),
          distinctUntilChanged((prev, curr) =>
            prev.nodeId == curr.nodeId
            && prev.preventRequery == curr.preventRequery
            && prev.query == curr.query
            && prev.selectedId == curr.selectedId
            && prev.webDokuId == curr.webDokuId
          ),
          switchMap(params => this.webdokuService.selectWebDoku(params.webDokuId)),
          switchMap((selectWdOk) => this.searchOrNavigate(selectWdOk, nodeId, selectedId, query, preventRequery != ''))
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    this.wdSubscription.unsubscribe();
  }

  searchOrNavigate(
    selectWdOk: boolean,
    nodeId: string,
    selectedId: string,
    query: string,
    preventRequery: boolean
  ): Observable<StructureList> {
    if (!selectWdOk) {
      this.documents = new StructureList();
      this.lastSelectedId = '';
      this.infoService.setStatus(`can't activate`);
      this.lastNodeId = '';
      this.closeSearch();
      return of(this.documents);
    }

    if (query == '') {
      return this.getDocumentNavigation(nodeId, selectedId);
    }
    return this.getSearchResults(query, preventRequery);
  }

  getDocumentNavigation(nodeId: string, selectedId: string): Observable<StructureList> {
    return this.documentService.getDocumentNavigationData(nodeId).pipe(
      tap((res) => {
        this.closeSearch({skipNavigation: true});
        this.documents = res;
        this.lastSelectedId = selectedId ?? '';
        this.infoService.setLoadedItems(res.data.length);
        this.lastNodeId = nodeId;
      })
    );
  }

  closeDetail() {
    this.hasDetail = false;
  }

  private getSearchResults(query: string, preventRequery: boolean): Observable<StructureList> {
    this.lastQuery = query;
    const searchOptions = SearchOptions.fromJson(query);
    return this.searchService.searchNodesByName(AppUrls.urlDocumentNavigation, searchOptions).pipe(
      tap((res) => {
        this.documents = res;
        this.lastSelectedId = '';
        this.infoService.setLoadedItems(res.data.length);
        this.lastNodeId = '';
        this.showSearch();
        if (res.data.length === 1 && !preventRequery) {
          this.navigate(res.data[0].id, query);
        } else {
          // fill text of search field
          let search = searchOptions.getSearchText();
          if (!RBCheck.isNullOrEmpty(res.searchText)) {
            search = res.searchText;
          }
          if (search != null && search.length > 0) {
            this.form.controls['searchField']?.patchValue(search);
          }
        }
        return res;
      })
    );
  }

  itemSelected(args: SelectionArguments) {
    if (args == undefined || args.id == undefined) {
      return;
    }

    // travel structure
    if (args.structureType == StructureType.Folder) {
      console.log(`start routing with ${this.route}`);
      this.router
        .navigate([], {
          queryParams: { selectedId: args.id },
          relativeTo: this.route,
          replaceUrl: true,
        })
        .then(() =>
          {
            if(this.settingsService.settings.documentMode.allowNewTabOnIconClick && args.action === SelectionType.IconSelected ) {
              const url = this.routerService.createExternalUrl(this.router.createUrlTree([{ nodeId: args.id }], { relativeTo: this.route }));
              window.open(url, '_blank');
              return Promise.resolve();
            }
            return this.router
            .navigate([{ nodeId: args.id }], { relativeTo: this.route })
            .then(() => console.log(`end routing with ${this.route}`))}
        );
      return;
    }

    this.selectItem(args);
  }

  private navigate(id: string, query: string, action?: SelectionType) {
    console.log(`start routing with ${this.route}`);

    let params = {};
    if (query == '') {
      params = { selectedId: id };
    } else {
      params = { query: query, pq: true };
    }

    // replace current url with the same url + selectedId
    this.router
      .navigate([], { queryParams: params, relativeTo: this.route, replaceUrl: true })
      .then(() =>
      {
        if(this.settingsService.settings.documentMode.allowNewTabOnIconClick && action === SelectionType.IconSelected ) {
          const url = this.routerService.createExternalUrl(this.router.createUrlTree([{ nodeId: id }], { relativeTo: this.route }));
          window.open(url, '_blank');
          return Promise.resolve();
        }
        return this.router
          .navigate([{ nodeId: id }], { relativeTo: this.route })
          .then(() => console.log(`end routing with ${this.route}`))

      }
              );
  }

  public showSearch() {
    this.searchVisible = true;
    this.allowSplitMode = false;

    this.splitviewObserver.notifyMoved({ gutterNum: 0 });
  }

  public closeSearch(args: {skipNavigation: boolean} = {skipNavigation: false}) {
    if (!this.searchVisible) {
      return;
    }
    this.searchVisible = false;
    this.allowSplitMode = true;

    this.splitviewObserver.notifyMoved({ gutterNum: 0 });

    this.form.controls['searchField'].patchValue('');
    if(args.skipNavigation) {
      return;
    }
    this.router
      .navigate(['./', {}], {
        queryParams: { query: undefined },
        relativeTo: this.route,
        replaceUrl: true,
      })
      .finally(() => {});
  }

  public submit() {
    const searchControl = this.form.controls['searchField'];
    this.searchFor(searchControl.value ?? '', false);
  }

  searchFor(searchText: string, isTokenSearch: boolean) {
    if (RBCheck.isNullOrEmpty(searchText)) {
      return;
    }
    const options = new SearchOptions();
    options.webDokuId = this.webDokuId;
    options.isTokenSearch = isTokenSearch;
    options.addSearchText(searchText);

    this.router.navigate(['./'], {
      queryParams: { query: this.routerService.serializeSearchOptions(options) },
      relativeTo: this.route,
    });
  }
}
